import { Button } from "react-migration/components/DeprecatedButton";
import classNames from "clsx";

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import * as React from "react";
import { FileRejection, useDropzone } from "react-dropzone";

export interface ImageUploadHandle {
  clear: () => void;
}

interface ImageUploadProps {
  "data-testid"?: string;
  file?: File;
  onUpload: (file?: File) => void;
}

interface Dimensions {
  height: number;
  width: number;
}

const ImageUploadComponent: React.ForwardRefRenderFunction<ImageUploadHandle, ImageUploadProps> = (
  { onUpload, "data-testid": dataTestId, file },
  forwardedRef
) => {
  const preview = useRef<string>("");
  const [{ height, width }, setDimensions] = useState<Dimensions>({ height: 0, width: 0 });
  const revokePreview = useCallback(() => {
    if (preview.current) {
      URL.revokeObjectURL(preview.current);
    }
    preview.current = "";
  }, [preview]);

  const processFile = useCallback((file: File, isNew?: boolean) => {
    preview.current = URL.createObjectURL(file);
    const image = new Image();
    image.src = preview.current;
    image.onload = (event) => {
      const { height, width } = event.target as HTMLImageElement;
      setDimensions({ height, width });
    };
    if (isNew) {
      onUpload(file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [rejectedFile, setRejectedFile] = useState<FileRejection>();
  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    noClick: false,
    noKeyboard: true,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    multiple: false,
    onDrop: ([acceptedFile], [fileRejection]) => {
      revokePreview();
      if (fileRejection) {
        setRejectedFile(fileRejection);
      } else if (acceptedFile) {
        processFile(acceptedFile, true);
      }
    },
  });

  useEffect(() => {
    revokePreview();
    if (file) {
      processFile(file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  useEffect(() => {
    return () => {
      // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
      revokePreview();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearDropzone = useCallback(() => {
    setRejectedFile(undefined);
    onUpload(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(forwardedRef, () => ({
    clear() {
      clearDropzone();
    },
  }));

  const containerBorderClassName = useMemo(() => {
    if (isDragAccept || isDragReject) {
      return "border-blue-700";
    }
    if (rejectedFile) {
      return "border-red-500";
    }
    return "border-border-action";
  }, [isDragAccept, isDragReject, rejectedFile]);

  const containerClassName = useMemo(
    () =>
      classNames(
        containerBorderClassName,
        "bg-white",
        "rounded-xl",
        "flex",
        "flex-col",
        "items-stretch",
        "justify-center",
        "p-4",
        isDragAccept || isDragReject ? "border-2" : "border m-px",
        isDragAccept || isDragReject ? "h-[148px]" : "h-[146px]"
      ),
    [isDragAccept, isDragReject, containerBorderClassName]
  );

  const iconClassName = useMemo(
    () =>
      classNames(
        "h-14",
        "w-14",
        "flex",
        "items-center",
        "justify-center",
        "border-8",
        "rounded-full",
        "mb-1",
        rejectedFile ? "border-border-action-destructive-hover" : "border-background-surface-neutral",
        rejectedFile ? "bg-[#F87171]" : "bg-background-surface-neutral-subtle"
      ),
    [rejectedFile]
  );

  const fileUploadedClassName = classNames("flex", "flex-row");

  const previewContainerClassName = classNames(
    "h-[72px]",
    "w-[72px]",
    "flex-none",
    "flex",
    "align-center",
    "border",
    "border-border-action",
    "rounded-sm"
  );

  const previewClassName = classNames("h-auto", "w-full", "object-contain");

  const previewTextClassName = classNames("ml-5", "flex", "justify-start", "flex-col");

  const emptyStateClassName = classNames("flex", "flex-col", "items-center");

  const onClickUpload = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
  };

  const renderContent = () => {
    if (file) {
      return (
        <div className={fileUploadedClassName}>
          <div className={previewContainerClassName}>
            <img className={previewClassName} src={preview.current} />
          </div>
          <div className={previewTextClassName}>
            <p className={classNames("text-sm", "m-0", "font-semibold")}>{file.name}</p>
            <p className={classNames("text-sm", "m-0")}>{height + "x" + width}</p>
            <p
              className={classNames(
                "text-sm",
                "text-text-base-active",
                "no-underline",
                "cursor-pointer",
                "hover:text-text-base-active-hover"
              )}
            >
              <span onClick={clearDropzone}>Replace</span>
            </p>
          </div>
        </div>
      );
    }
    if (rejectedFile) {
      return (
        <>
          <div className={classNames(iconClassName)}>
            <i className="text-xl text-white icon-lt-error-warning-line" />
          </div>
          <p className={classNames("text-sm", "text-red-500")}>File size is too large</p>
        </>
      );
    }
    return (
      <div className={emptyStateClassName}>
        <div className={iconClassName}>
          <i className="text-xl icon-lt-upload-cloud-2-line" />
        </div>
        <p className={classNames("text-sm", "m-0")}>
          <Button.Borderless className="px-1!" onClick={(e) => onClickUpload(e)}>
            Click to upload
          </Button.Borderless>
          or drag and drop
        </p>
        <p className={classNames("text-xs", "m-0")}>PNG or JPG (max. 500x500px)</p>
      </div>
    );
  };

  return (
    <div
      {...getRootProps({
        className: containerClassName,
      })}
      data-testid={dataTestId || "image-upload"}
    >
      <input {...getInputProps()} data-testid="image-upload-input" />
      {renderContent()}
    </div>
  );
};

export const ImageUpload = forwardRef(ImageUploadComponent);
